import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import {
  Box,
  Dialog,
  DialogContent,
  FormControl,
  Grid,
  Input,
  InputLabel,
  MenuItem,
  Rating,
  Select,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { assetKeys, entityKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import iconMapping from "../../../../icons/IconMapping";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { AssetTypes } from "../../../../utils/constants";

const AssetCreationOrEditModal = ({ isOpen, onClose, asset = null }) => {
  const colors = useColors();
  const [assetName, setAssetName] = useState(asset ? asset.name : "");
  const [assetType, setAssetType] = useState(asset ? asset.type : "");
  const [importanceScore, setImportanceScore] = useState(
    asset ? asset.importance : 0
  );
  const [isLoadingSubmission, setIsLoadingSubmission] = useState(false);
  const {
    getAssetsByName,
    createAsset,
    updateAsset,
    trackEvent,
    refreshEntitiesOverview,
  } = useAPI();
  const queryClient = useQueryClient();

  const isInEditMode = asset !== null;

  const isAnyFieldEmpty = () => {
    return (
      !assetName ||
      assetType === "" ||
      importanceScore === 0 ||
      importanceScore === null
    );
  };

  const onSubmitClick = async () => {
    if (isAnyFieldEmpty()) return;

    setIsLoadingSubmission(true);

    if (!isInEditMode) {
      const assets = await getAssetsByName(assetName);
      if (assets.data.length > 0) {
        alert("Asset with this name already exists!");
        setIsLoadingSubmission(false);
        return;
      }
    }

    const assetData = {
      name: assetName,
      type: assetType,
      importance: importanceScore,
      entity_ids: asset?.entity_ids || [],
    };

    if (isInEditMode) {
      assetData.asset_id = asset.asset_id;
      handleAssetUpdate(assetData);
    } else handleAssetCreation(assetData);
  };

  useEffect(() => {
    if (isOpen) trackEvent("User opened the asset creation modal");
  }, [isOpen]);

  const handleAssetCreation = async (assetData) => {
    trackEvent(`User is creating a new asset named ${assetData.name}`).then();
    await createAsset(assetData)
      .then(() => {
        Toast.success({
          text: (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="caption">
                Asset {assetData.name} created successfully!
              </Typography>
              <Typography variant="caption">
                Risk data for this asset is temporarily unavailable (N/A) and
                will update after the next scan.
              </Typography>
            </Box>
          ),
        });
        queryClient.invalidateQueries({ queryKey: assetKeys.all });
        refreshEntitiesOverview().then(() => {
          queryClient.invalidateQueries({
            queryKey: entityKeys.entitiesOverview(),
          });
        });
        setIsLoadingSubmission(false);
        onClose();
      })
      .catch(() => {
        Toast.error("An error occurred while creating the asset.");
      });
  };

  const handleAssetUpdate = async (assetData) => {
    trackEvent(`User is updating asset named ${assetData.name}`).then();
    await updateAsset(assetData)
      .then(() => {
        Toast.success(`Asset ${assetData.name} updated successfully!`);
        queryClient.invalidateQueries({ queryKey: assetKeys.all });
        refreshEntitiesOverview().then(() => {
          queryClient.invalidateQueries({
            queryKey: entityKeys.entitiesOverview(),
          });
        });
        setIsLoadingSubmission(false);
        onClose();
      })
      .catch((error) => {
        Toast.error("An error occurred while updating the asset.");
      });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "10px",
          boxShadow: "0px 2px 6px " + colors.primary_2percent,
        },
      }}
    >
      <DialogContent
        sx={{
          width: 370,
          padding: 3,
        }}
      >
        <Grid container direction="column">
          {/* Asset name */}
          <Grid item>
            <FormControl variant="standard" fullWidth>
              <InputLabel
                shrink
                sx={{
                  "&.MuiInputLabel-shrink": {
                    color: colors.grey_300 + " !important",
                    fontWeight: 600,
                  },
                }}
              >
                Asset
              </InputLabel>
              <Input
                placeholder="Asset Name"
                defaultValue={assetName}
                onChange={(event) => setAssetName(event.target.value)}
                disableUnderline
                sx={{
                  "& .MuiInput-input": {
                    borderBottom: `1px solid ${colors.grey_300} !important`,
                  },
                }}
              />
            </FormControl>
          </Grid>
          {/* Type */}
          <Grid item sx={{ mt: 2 }}>
            <FormControl variant="standard" fullWidth>
              <InputLabel
                shrink
                sx={{
                  "&.MuiInputLabel-shrink": {
                    color: colors.grey_300,
                    fontWeight: 600,
                  },
                }}
              >
                Asset Category
              </InputLabel>
              {assetType === "" && (
                <InputLabel
                  shrink={false}
                  sx={{
                    color: "text.disabled",
                    "&&.Mui-focused": {
                      color: "text.disabled",
                    },
                  }}
                >
                  Select a category
                </InputLabel>
              )}
              <Select
                value={assetType}
                onChange={(event) => setAssetType(event.target.value)}
                disableUnderline
                sx={{
                  "& .MuiInput-input": {
                    borderBottom: `1px solid ${colors.grey_300}`,
                  },
                }}
              >
                {AssetTypes.map((type) => (
                  <MenuItem value={type} key={type}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <SvgToIcon IconLogo={iconMapping[type]} />
                      {type}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* Importance */}
          <Grid item sx={{ mt: 2 }}>
            <FormControl variant="standard" fullWidth>
              <Typography
                variant="caption"
                sx={{
                  color: colors.assetModalLabel,
                  fontWeight: 600,
                  marginBottom: 1,
                }}
              >
                Importance
              </Typography>
              <Rating
                sx={{ marginLeft: "-4px" }}
                name="Importance"
                value={importanceScore}
                onChange={(event, newValue) => {
                  if (newValue) setImportanceScore(newValue);
                }}
                max={5}
                icon={
                  <AttachMoneyIcon
                    fontSize="inherit"
                    style={{ color: "green" }}
                  />
                }
                emptyIcon={<AttachMoneyOutlinedIcon fontSize="inherit" />}
              />
            </FormControl>
          </Grid>
          {/* Button */}
          <Grid item sx={{ mt: 3 }} justifyItems="center">
            {isLoadingSubmission ? (
              <LoadingIcon size={16} />
            ) : (
              <Box display="flex" justifyContent="center">
                <PrimaryButton
                  sx={{
                    textTransform: "none",
                    fontWeight: "600",
                    boxShadow: "none",
                  }}
                  onClick={onSubmitClick}
                  disabled={isAnyFieldEmpty()}
                >
                  Done
                </PrimaryButton>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
export default AssetCreationOrEditModal;
